/* eslint-disable @typescript-eslint/no-explicit-any */
import Observable from 'zen-observable-ts';

import {
  Amplify,
  browserOrNode,
  ConsoleLogger as Logger,
  INTERNAL_AWS_APPSYNC_PUBSUB_PROVIDER,
  INTERNAL_AWS_APPSYNC_REALTIME_PUBSUB_PROVIDER
} from '@aws-amplify/core';
import { PubSubProvider, PubSubOptions, ProvidertOptions } from '@aws-amplify/pubsub/lib/types';
import { AWSAppSyncProvider, AWSAppSyncRealTimeProvider } from '@aws-amplify/pubsub/lib/Providers';

import { MqttOverWSProvider } from '@aws-amplify/pubsub/lib/Providers';

const { isNode } = browserOrNode();
const logger = new Logger('PubSub');

export class PubSubClass {
  private _options: PubSubOptions;

  private _pluggables: PubSubProvider[];

  /**
   * Internal instance of AWSAppSyncProvider used by the API category to subscribe to AppSync
   */
  private _awsAppSyncProvider: AWSAppSyncProvider | undefined;

  /**
   * Internal instance of AWSAppSyncRealTimeProvider used by the API category to subscribe to AppSync
   */
  private _awsAppSyncRealTimeProvider: AWSAppSyncRealTimeProvider | undefined;

  /**
   * Lazy instantiate AWSAppSyncProvider when it is required by the API category
   */
  private get awsAppSyncProvider() {
    if (!this._awsAppSyncProvider) {
      this._awsAppSyncProvider = new AWSAppSyncProvider(this._options);
    }
    return this._awsAppSyncProvider;
  }

  /**
   * Lazy instantiate AWSAppSyncRealTimeProvider when it is required by the API category
   */
  private get awsAppSyncRealTimeProvider() {
    if (!this._awsAppSyncRealTimeProvider) {
      this._awsAppSyncRealTimeProvider = new AWSAppSyncRealTimeProvider(this._options);
    }
    return this._awsAppSyncRealTimeProvider;
  }

  /**
   * Initialize PubSub with AWS configurations
   *
   * @param {PubSubOptions} options - Configuration object for PubSub
   */
  constructor(options: PubSubOptions | any) {
    this._options = options;
    logger.debug('PubSub Options', this._options);
    this._pluggables = [];
    this.subscribe = this.subscribe.bind(this);
  }

  public getModuleName() {
    return 'PubSub';
  }

  /**
   * Configure PubSub part with configurations
   *
   * @param {PubSubOptions} config - Configuration for PubSub
   * @return {Object} - The current configuration
   */
  configure(options: PubSubOptions) {
    const opt = options ? options.PubSub || options : {};
    logger.debug('configure PubSub', { opt });

    this._options = Object.assign({}, this._options, opt);

    this._pluggables.map((pluggable) => pluggable.configure(this._options));

    return this._options;
  }

  /**
   * add plugin into Analytics category
   * @param {Object} pluggable - an instance of the plugin
   */
  public async addPluggable(pluggable: PubSubProvider) {
    if (pluggable && pluggable.getCategory() === 'PubSub') {
      this._pluggables.push(pluggable);

      const config = pluggable.configure(this._options);

      return config;
    }
  }

  private getProviderByName(providerName: any) {
    if (providerName === INTERNAL_AWS_APPSYNC_PUBSUB_PROVIDER) {
      return this.awsAppSyncProvider;
    }
    if (providerName === INTERNAL_AWS_APPSYNC_REALTIME_PUBSUB_PROVIDER) {
      return this.awsAppSyncRealTimeProvider;
    }

    return this._pluggables.find((pluggable) => pluggable.getProviderName() === providerName);
  }

  private getProviders(options: ProvidertOptions = {}) {
    const { provider: providerName } = options;
    if (!providerName) {
      return this._pluggables;
    }

    const provider = this.getProviderByName(providerName);
    if (!provider) {
      throw new Error(`Could not find provider named ${providerName}`);
    }

    return [provider];
  }

  public restartAWSIoTProvider(mqttEndpoint: string) {
    logger.debug('Restarting MQTTProvider...');
    this._pluggables[0] = new MqttOverWSProvider({
      aws_pubsub_endpoint: mqttEndpoint,
      aws_appsync_dangerously_connect_to_http_endpoint_for_testing: true
    });
  }

  async publish(topics: string[] | string, msg: any, options?: ProvidertOptions) {
    return Promise.all(this.getProviders(options).map((provider) => provider.publish(topics, msg, options)));
  }

  subscribe(topics: string[] | string, options?: ProvidertOptions): Observable<any> {
    if (isNode) {
      throw new Error('Subscriptions are not supported in Node');
    }

    const providers = this.getProviders(options);

    return new Observable((observer) => {
      const observables = providers.map((provider) => ({
        provider,
        observable: provider.subscribe(topics, options)
      }));

      const subscriptions = observables.map(({ provider, observable }) =>
        observable.subscribe({
          start: console.error,
          next: (value) => observer.next({ provider, value }),
          error: (error) => observer.error({ provider, error })
          // complete: observer.complete, // TODO: when all completed, complete the outer one
        })
      );

      return () => subscriptions.forEach((subscription) => subscription.unsubscribe());
    });
  }
}
export const PubSub = new PubSubClass(null);
Amplify.register(PubSub);
